import * as React from "react";
import ImageFlowObject from "../../model/ImageFlowObject";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import TextFlowObject from "../../model/TextFlowObject";
import Color from "../../model/Color";
import { MARK_ALL_INACTIVE, MARK_INACTIVE } from "../fsm/types";
import { MachineContext } from "../fsm/useMachine";
import { AVMediaView } from "./av-media";
import { MMOInteractivity } from "./mmo-interactor";
import { ShapeView } from "./shape";
import { TextView } from "./Text";
import _debounce from "lodash.debounce"
import { useEditorCoordinator } from "../text_editor/editorCoordinator";
import { addFileByType } from "../control_bar/adding";

/**
 * Safely get rgba string from a color that may be a Color instance or plain object
 * (xstate context cloning can strip prototypes)
 */
function safeColorToRgba(color: Color | { r: number; g: number; b: number; a?: number } | undefined): string {
    if (!color) return '#333';
    if (typeof (color as Color).asRgba === 'function') {
        return (color as Color).asRgba();
    }
    // Plain object fallback
    const { r, g, b, a = 1 } = color as { r: number; g: number; b: number; a?: number };
    return `rgba(${r},${g},${b},${a})`;
}

interface FlowCanvasOptions {
    size: { width: number, height: number, ratio: number }
}


export const FlowCanvas: React.FC<FlowCanvasOptions> = ({ size }) => {
    const [current, send] = React.useContext(MachineContext)
    const screen = current.context.flow.screens[current.context.index]
    const [isDragging, setDragging] = React.useState(false)

    // Drag-and-drop file state
    const [isFileDragOver, setIsFileDragOver] = React.useState(false)
    const dragCounterRef = React.useRef(0)

    // Guard against screen being undefined during transitions
    if (!screen) return null
    const [allowEdit, setAllowEdit] = React.useState<{[key: string]: boolean}>({})
    const { flushAllPendingUpdates } = useEditorCoordinator()

    // Store current and send in refs to avoid recreating the callback
    const currentRef = React.useRef(current)
    const sendRef = React.useRef(send)
    React.useEffect(() => {
        currentRef.current = current
        sendRef.current = send
    }, [current, send])

    // Clear all edit states when screen changes
    const prevIndexRef = React.useRef(current.context.index)
    React.useEffect(() => {
        // Only run when index actually changes, not on every render
        if (prevIndexRef.current !== current.context.index) {
            console.log('[Screen] Screen index changed, flushing pending updates')
            // Flush all pending text updates BEFORE clearing edit states
            flushAllPendingUpdates().then(() => {
                console.log('[Screen] Flush complete, clearing edit states')
                setAllowEdit({})
                send({ type: MARK_ALL_INACTIVE })
            })
            prevIndexRef.current = current.context.index
        }
    }, [current.context.index])

    React.useEffect(() => {
        const container = document.getElementById("container")
        if (!container) return

        const outsideClickHandler = (ev: MouseEvent) => {
            if (isDragging) {
                return
            }

            const target = ev.target as HTMLElement

            // Check if we're in edit mode
            const isEditing = currentRef.current.value.textboxes === 'active'

            // If we're editing and clicked anywhere (container or any object that's not the ProseMirror editor)
            if (isEditing) {
                const isProseMirror = target.classList.contains('ProseMirror') || target.closest('.ProseMirror')
                if (!isProseMirror) {
                    setAllowEdit({})
                    sendRef.current({ type: MARK_INACTIVE })
                }
            }

            // Deselect when clicking directly on the container background (not on any MMO)
            const isInsideMMO = target.closest('.mmo') !== null
            const isInsideMoveable = target.closest('.moveable-control-box') !== null ||
                                     target.closest('.moveable-line') !== null ||
                                     target.closest('.moveable-control') !== null
            const isInsideControlBar = target.closest('.control-panel') !== null ||
                                       target.closest('.editor-ai-panel') !== null ||
                                       target.closest('.ai-panel') !== null

            // Deselect if clicking on container background (not inside any interactive element)
            if (!isInsideMMO && !isInsideMoveable && !isInsideControlBar && currentRef.current.context.currentObject) {
                console.log('[Screen] Deselecting - clicked on background')
                sendRef.current({ type: MARK_ALL_INACTIVE })
            }
        }

        // Use mousedown for more responsive deselection (fires before click)
        container.addEventListener("mousedown", outsideClickHandler)
        return () => {
            container.removeEventListener("mousedown", outsideClickHandler)
        }
    }, [isDragging])


    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        dragCounterRef.current++;
        if (e.dataTransfer.types.includes("Files")) {
            setIsFileDragOver(true);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setIsFileDragOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsFileDragOver(false);
        const files = e.dataTransfer.files;
        for (let i = 0; i < files.length; i++) {
            addFileByType(
                files[i],
                current.context.flow.viewSize,
                send,
                current.context.index
            );
        }
    };

    return (<div
        className={`container${isFileDragOver ? " drag-over" : ""}`}
        id={"container"}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
            width: `${size.width}px`,
            height: `${size.height}px`,
            backgroundColor: safeColorToRgba(screen.FlowScreenModelBackgroundColorKey),
            margin: "0 auto",
            position: "relative",
            }}>
        {screen.mixedMediaObjects.map((obj) => {
            if (obj.type === "text") {
                return <MMOInteractivity
                    mmo={obj}
                    size={size}
                    allowedWhenActive={{ resizable: true, rotatable: true, draggable: true, keepRatio: false }}
                    isCurrentObject={obj.id === current.context.currentObject?.id}
                    hasAnySelection={!!current.context.currentObject}
                    send={send}
                    dragging={[isDragging, setDragging]}
                    key={obj.id}
                    display={(props) => <TextView
                        {...props}
                        mmo={props.mmo as TextFlowObject}
                        editing={[allowEdit, setAllowEdit]}
                        ref={props.forwardedRef}
                    />}
                />
            } else if (obj.type === 'shape') {
                return <MMOInteractivity
                    mmo={obj}
                    size={size}
                    //@ts-ignore
                    allowedWhenActive={{ resizable: true, rotatable: true, draggable: true, keepRatio: false }}
                    isCurrentObject={obj.id === current.context.currentObject?.id}
                    hasAnySelection={!!current.context.currentObject}
                    send={send}
                    dragging={[isDragging, setDragging]}
                    key={obj.id}
                    display={(props) =>
                        <ShapeView
                            {...props}
                            mmo={props.mmo as ShapeFlowObject}
                            ref={props.forwardedRef}
                        />}
                />
            } else {
                return <MMOInteractivity
                    mmo={obj}
                    size={size}
                    //@ts-ignore
                    allowedWhenActive={{ resizable: true, rotatable: true, draggable: true, keepRatio: true }}
                    isCurrentObject={obj.id === current.context.currentObject?.id}
                    hasAnySelection={!!current.context.currentObject}
                    send={send}
                    dragging={[isDragging, setDragging]}
                    key={obj.id}
                    display={(props) =>
                        <AVMediaView {...props}
                            mmo={props.mmo as ImageFlowObject}
                            ref={props.forwardedRef}
                        />}
                />
            }
        })}
    </div>)
}

function recursiveAscentToId(element: HTMLElement | SVGSVGElement): string {
    if (element.id.length > 0) return element.id
    else return recursiveAscentToId(element.parentElement!)
}